import React, { useState, useEffect, useReducer, useContext } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import Paper from '@material-ui/core/Paper';

import TicketListItem from '../TicketListItemCustom';
import TicketsListSkeleton from '../TicketsListSkeleton';

import useTickets from '../../hooks/useTickets';
import { useIdleStatusUpdates } from '../../hooks/useIdleStatusUpdates';
import { i18n } from '../../translate/i18n';
import { AuthContext } from '../../context/Auth/AuthContext';
import { SocketContext } from '../../context/Socket/SocketContext';

const useStyles = makeStyles(theme => ({
  ticketsListWrapper: {
    position: 'relative',
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
    overflow: 'hidden',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },

  ticketsList: {
    flex: 1,
    maxHeight: '100%',
    overflowY: 'scroll',
    ...theme.scrollbarStyles,
    borderTop: '2px solid rgba(0, 0, 0, 0.12)',
  },

  ticketsListHeader: {
    color: 'rgb(67, 83, 105)',
    zIndex: 2,
    backgroundColor: 'white',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  ticketsCount: {
    fontWeight: 'normal',
    color: 'rgb(104, 121, 146)',
    marginLeft: '8px',
    fontSize: '14px',
  },

  noTicketsText: {
    textAlign: 'center',
    color: 'rgb(104, 121, 146)',
    fontSize: '14px',
    lineHeight: '1.4',
  },

  noTicketsTitle: {
    textAlign: 'center',
    fontSize: '16px',
    fontWeight: '600',
    margin: '0px',
  },

  noTicketsDiv: {
    display: 'flex',
    height: '100px',
    margin: 40,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const reducer = (state, action) => {
  if (action.type === 'LOAD_TICKETS') {
    const newTickets = action.payload;

    newTickets.forEach(ticket => {
      const ticketIndex = state.findIndex(t => t.id === ticket.id);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
        if (ticket.unreadMessages > 0) {
          state.unshift(state.splice(ticketIndex, 1)[0]);
        }
      } else {
        state.push(ticket);
      }
    });

    return [...state];
  }

  if (action.type === 'RESET_UNREAD') {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex(t => t.id === ticketId);
    if (ticketIndex !== -1) {
      // Preservar o idleStatus atual ao resetar unreadMessages
      const currentIdleStatus = state[ticketIndex].idleStatus;
      state[ticketIndex].unreadMessages = 0;

      // Se o backend não enviou idleStatus, manter o atual
      if (currentIdleStatus && !state[ticketIndex].idleStatus) {
        state[ticketIndex].idleStatus = currentIdleStatus;
      }
    }

    return [...state];
  }

  if (action.type === 'UPDATE_TICKET') {
    const ticket = action.payload;

    const ticketIndex = state.findIndex(t => t.id === ticket.id);
    if (ticketIndex !== -1) {
      // Preservar o idleStatus atual se a atualização não incluir um novo
      const currentIdleStatus = state[ticketIndex].idleStatus;
      state[ticketIndex] = ticket;

      // Se o ticket atualizado não tem idleStatus mas o anterior tinha, preservar
      if (currentIdleStatus && !ticket.idleStatus) {
        state[ticketIndex].idleStatus = currentIdleStatus;
      }
    } else {
      state.unshift(ticket);
    }

    return [...state];
  }

  if (action.type === 'UPDATE_TICKET_UNREAD_MESSAGES') {
    const ticket = action.payload;

    const ticketIndex = state.findIndex(t => t.id === ticket.id);
    if (ticketIndex !== -1) {
      // Preservar o idleStatus atual ao atualizar mensagens não lidas
      const currentIdleStatus = state[ticketIndex].idleStatus;
      state[ticketIndex] = ticket;

      // Se o ticket atualizado não tem idleStatus mas o anterior tinha, preservar
      if (currentIdleStatus && !ticket.idleStatus) {
        state[ticketIndex].idleStatus = currentIdleStatus;
      }

      state.unshift(state.splice(ticketIndex, 1)[0]);
    } else {
      state.unshift(ticket);
    }

    return [...state];
  }

  if (action.type === 'UPDATE_TICKET_CONTACT') {
    const contact = action.payload;
    const ticketIndex = state.findIndex(t => t.contactId === contact.id);
    if (ticketIndex !== -1) {
      state[ticketIndex].contact = contact;
    }
    return [...state];
  }

  if (action.type === 'DELETE_TICKET') {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex(t => t.id === ticketId);
    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1);
    }

    return [...state];
  }

  if (action.type === 'RESET') {
    return [];
  }

  if (action.type === 'FORCE_UPDATE') {
    return [...state];
  }
};

const TicketsListCustom = props => {
  const {
    status,
    searchParam,
    tags,
    users,
    showAll,
    selectedQueueIds,
    updateCount,
    onTicketAction,
    style,
    refreshKey,
  } = props;
  const classes = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);
  const { profile, queues } = user;

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: 'RESET' });
    setPageNumber(1);
  }, [status, searchParam, dispatch, showAll, tags, users, selectedQueueIds]);

  const { tickets, hasMore, loading } = useTickets({
    pageNumber,
    searchParam,
    status,
    showAll,
    tags: JSON.stringify(tags),
    users: JSON.stringify(users),
    queueIds: JSON.stringify(selectedQueueIds),
  });

  // Hook para atualizações de ociosidade via WebSocket
  const { idleStatusUpdates } = useIdleStatusUpdates();

  useEffect(() => {
    const queueIds = queues.map(q => q.id);
    const filteredTickets = tickets.filter(
      t => queueIds.indexOf(t.queueId) > -1
    );

    if (profile === 'user') {
      dispatch({ type: 'LOAD_TICKETS', payload: filteredTickets });
    } else {
      dispatch({ type: 'LOAD_TICKETS', payload: tickets });
    }
  }, [tickets, status, searchParam, queues, profile]);

  // Aplicar atualizações de ociosidade via WebSocket/API
  useEffect(() => {
    if (Object.keys(idleStatusUpdates).length === 0) {
      return;
    }

    Object.entries(idleStatusUpdates).forEach(([ticketId, idleStatus]) => {
      // Garantir que ticketId seja um número
      const numericTicketId = parseInt(ticketId);

      const existingTicketIndex = ticketsList.findIndex(
        t => t.id === numericTicketId
      );

      if (existingTicketIndex !== -1) {
        const updatedTicket = {
          ...ticketsList[existingTicketIndex],
          idleStatus,
        };
        dispatch({
          type: 'UPDATE_TICKET',
          payload: updatedTicket,
        });
      }
    });
  }, [idleStatusUpdates]); // Removido ticketsList das dependências para evitar loops

  useEffect(() => {
    const companyId = localStorage.getItem('companyId');
    const socket = socketManager.getSocket(companyId);

    // Lista de filas vazia significa "sem filtro de fila", igual ao backend
    // (ListTicketsService só aplica o filtro quando queueIds.length > 0).
    // Antes, um usuário sem filas atribuídas — o caso do admin — via a lista
    // carregar pelo HTTP e nunca mais atualizar, porque todo evento de socket
    // caía no indexOf() === -1 e era descartado.
    const hasQueueFilter = selectedQueueIds.length > 0;

    const matchesQueueFilter = queueId =>
      !hasQueueFilter || !queueId || selectedQueueIds.indexOf(queueId) > -1;

    const shouldUpdateTicket = ticket =>
      (!ticket.userId || ticket.userId === user?.id || showAll) &&
      matchesQueueFilter(ticket.queueId);

    const notBelongsToUserQueues = ticket =>
      hasQueueFilter &&
      ticket.queueId &&
      selectedQueueIds.indexOf(ticket.queueId) === -1;

    socket.on('ready', () => {
      if (status) {
        socket.emit('joinTickets', status);
      } else {
        socket.emit('joinNotification');
      }
    });

    // O backend também emite status-change / user-change / queue-change /
    // deal-change com o ticket completo. Tratando só 'update', o ticket não
    // migrava entre as abas nem os contadores mexiam quando alguém mudava
    // status, fila ou responsável.
    const TICKET_UPDATE_ACTIONS = [
      'update',
      'status-change',
      'user-change',
      'queue-change',
      'deal-change',
    ];

    socket.on(`company-${companyId}-ticket`, data => {
      if (data.action === 'updateUnread') {
        dispatch({
          type: 'RESET_UNREAD',
          payload: data.ticketId,
        });
      }

      const isUpdate =
        TICKET_UPDATE_ACTIONS.includes(data.action) && data.ticket;

      if (isUpdate && shouldUpdateTicket(data.ticket)) {
        if (data.ticket.status === status) {
          dispatch({
            type: 'UPDATE_TICKET',
            payload: data.ticket,
          });
        } else {
          // Mudou de status: sai desta aba e aparece na aba correspondente
          dispatch({ type: 'DELETE_TICKET', payload: data.ticket.id });
        }
      }

      if (isUpdate && notBelongsToUserQueues(data.ticket)) {
        dispatch({ type: 'DELETE_TICKET', payload: data.ticket.id });
      }

      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_TICKET', payload: data.ticketId });
      }
    });

    socket.on(`company-${companyId}-appMessage`, data => {
      const queueIds = queues.map(q => q.id);
      if (
        profile === 'user' &&
        queueIds.length > 0 &&
        (queueIds.indexOf(data.ticket?.queue?.id) === -1 ||
          data.ticket.queue === null)
      ) {
        return;
      }

      if (
        data.action === 'create' &&
        shouldUpdateTicket(data.ticket) &&
        (status === undefined || data.ticket.status === status)
      ) {
        dispatch({
          type: 'UPDATE_TICKET_UNREAD_MESSAGES',
          payload: data.ticket,
        });
      }
    });

    socket.on(`company-${companyId}-contact`, data => {
      if (data.action === 'update') {
        dispatch({
          type: 'UPDATE_TICKET_CONTACT',
          payload: data.contact,
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [
    status,
    showAll,
    user,
    selectedQueueIds,
    tags,
    users,
    profile,
    queues,
    socketManager,
  ]);

  useEffect(() => {
    if (typeof updateCount === 'function') {
      updateCount(ticketsList.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);

  // Forçar atualização dos indicadores de ociosidade a cada minuto
  useEffect(() => {
    const interval = setInterval(() => {
      // Forçar re-render dos componentes para atualizar os tempos
      dispatch({ type: 'FORCE_UPDATE' });
    }, 60000); // Atualizar a cada minuto

    return () => clearInterval(interval);
  }, []);

  const loadMore = () => {
    setPageNumber(prevState => prevState + 1);
  };

  const handleScroll = e => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <Paper className={classes.ticketsListWrapper} style={style}>
      <Paper
        square
        name='closed'
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {ticketsList.length === 0 && !loading ? (
            <div className={classes.noTicketsDiv}>
              <span className={classes.noTicketsTitle}>
                {i18n.t('ticketsList.noTicketsTitle')}
              </span>
              <p className={classes.noTicketsText}>
                {i18n.t('ticketsList.noTicketsMessage')}
              </p>
            </div>
          ) : (
            <>
              {ticketsList.map(ticket => (
                <TicketListItem
                  ticket={ticket}
                  key={ticket.id}
                  onTicketAction={onTicketAction}
                  refreshKey={refreshKey || 0}
                />
              ))}
            </>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>
    </Paper>
  );
};

export default TicketsListCustom;
